import { Close } from "@mui/icons-material";
import {
  Alert,
  Icon,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import { desktopSx } from "src/utils/styles/modalStyles";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { useTransferProductsMutation } from "src/api/services/productsApi";

interface Props {
  open: boolean;
  onClose: () => void;
}

const TransferProductsModal = ({ open, onClose }: Props) => {
  const { selected, setSelected } = useSelection();
  const nodeEnv = import.meta.env.VITE_NODE_ENV;
  const [loading, setLoading] = useState<boolean>(false);
  const errLog = useSentryLogger("error");
  const [transferProducts] = useTransferProductsMutation();
  const [alert, setAlert] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setSelected([]);
    setAlert(null);
    onClose();
  }, [onClose, setSelected]);

  const handleTransfer = useCallback(async () => {
    setLoading(true);

    try {
      const transfer = await transferProducts(selected);

      setAlert(
        transfer.data?.message ??
          "Successfully transferred the selected products",
      );

      handleClose();
    } catch (err) {
      errLog("Unable to transfer the selected products", err);
    } finally {
      setLoading(false);
    }
  }, [errLog, handleClose, selected, transferProducts]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Stack sx={desktopSx} gap={3}>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" component="h5" fontWeight={700}>
            Transfer Products
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Alert severity="warning">
          <Typography
            color="warning"
            sx={{ textDecoration: "underline" }}
            variant="h6"
          >
            Products selected
          </Typography>
          <Typography fontWeight={600} variant="h6">
            {selected.join(", ")}
          </Typography>
        </Alert>
        <Stack gap={3} alignItems={"center"}>
          <Typography variant="h5" fontWeight={700}>
            {nodeEnv === "production" ? "Production" : "Staging"}
          </Typography>
          <Icon fontSize="large">
            <ImportExportIcon fontSize="large" />
          </Icon>
          <Typography variant="h5" fontWeight={700}>
            {nodeEnv === "production" ? "Staging" : "Production"}
          </Typography>
        </Stack>
        {alert == null ? (
          <LoadingButton loading={loading} onClick={handleTransfer}>
            Complete transfer
          </LoadingButton>
        ) : (
          <Alert severity="success">{alert}</Alert>
        )}
      </Stack>
    </Modal>
  );
};

export default TransferProductsModal;
